import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { LinkCard, PageHead, Section } from '../components/ui';
import { monthFirst, monthKey, monthName } from '../lib/format';

type Waiting = {
  drafts: number | null;
  pictures: number | null;
  ending: number | null;
  paperwork: number | null;
  booksClosed: boolean | null; // null = no row for this month yet
};

type Kpis = {
  salons: number | null;
  mentors: number | null;
  apprentices: number | null;
  lessons7d: number | null;
  games: number | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Q = any;
async function count(table: string, filter?: (q: Q) => Q): Promise<number | null> {
  try {
    let q: Q = supabase.from(table).select('*', { count: 'exact', head: true });
    if (filter) q = filter(q);
    const { count: n, error } = await q;
    if (error) {
      console.warn(`[backbar] count(${table}) failed:`, error.message);
      return null;
    }
    return n ?? 0;
  } catch (e) {
    console.warn(`[backbar] count(${table}) threw:`, e);
    return null;
  }
}

export function Home() {
  const thisMonth = monthKey();
  const [w, setW] = useState<Waiting>({ drafts: null, pictures: null, ending: null, paperwork: null, booksClosed: null });
  const [k, setK] = useState<Kpis>({ salons: null, mentors: null, apprentices: null, lessons7d: null, games: null });
  const [booksLoaded, setBooksLoaded] = useState(false);

  useEffect(() => {
    const now = new Date();
    const soon = new Date(now.getTime() + 30 * 86400_000).toISOString();
    const nowIso = now.toISOString();
    const sevenAgo = new Date(now.getTime() - 7 * 86400_000).toISOString();

    (async () => {
      const [drafts, pictures, ending, paperwork, booksRow] = await Promise.all([
        count('module_drafts'),
        count('curriculum_images', (q) => q.in('status', ['missing', 'redo'])),
        count('entitlement_grants', (q) => q.is('revoked_at', null).gte('ends_at', nowIso).lte('ends_at', soon)),
        count('stylists', (q) => q.eq('role', 'apprentice').in('state_paperwork_status', ['not_filed', 'unsure'])),
        supabase.from('books_months').select('month, closed_at').eq('month', monthFirst(thisMonth)).maybeSingle(),
      ]);
      const row = booksRow.data as { closed_at: string | null } | null;
      setW({
        drafts,
        pictures: pictures ?? 0,
        ending,
        paperwork,
        booksClosed: row ? Boolean(row.closed_at) : null,
      });
      setBooksLoaded(true);
    })();

    (async () => {
      const [salons, mentors, apprentices, lessons7d, games] = await Promise.all([
        count('salons'),
        count('stylists', (q) => q.in('role', ['owner', 'educator'])),
        count('stylists', (q) => q.eq('role', 'apprentice')),
        count('lesson_progress', (q) => q.eq('status', 'completed').gte('completed_at', sevenAgo)),
        count('practice_games', (q) => q.eq('is_enabled', true)),
      ]);
      setK({ salons, mentors, apprentices, lessons7d, games });
    })();
  }, [thisMonth]);

  const booksLine = !booksLoaded
    ? 'Checking…'
    : w.booksClosed === null
      ? `${monthName(monthFirst(thisMonth))} has no numbers yet.`
      : w.booksClosed
        ? `${monthName(monthFirst(thisMonth))} is closed.`
        : `${monthName(monthFirst(thisMonth))} is still open.`;

  return (
    <>
      <PageHead
        back={null}
        title="Things waiting for you"
        sub="Each card opens the page where the work happens. A gray card means nothing is waiting there."
      />

      <div className="lp-cards">
        <LinkCard
          to="/lessons"
          count={w.drafts}
          title="Lesson changes not published"
          line={w.drafts ? 'Apprentices see the old text until you publish.' : 'Every lesson is published.'}
        />
        <LinkCard
          to="/illustrations"
          count={w.pictures}
          title="Pictures missing"
          line={w.pictures ? 'Lessons with a picture slot and no picture yet.' : 'Every picture slot is filled.'}
        />
        <LinkCard
          to="/money"
          count={w.ending}
          title="Free access ending within 30 days"
          line={w.ending ? 'Extend it on Money, or let it end.' : 'No free access is about to end.'}
        />
        <LinkCard
          to="/people"
          count={w.paperwork}
          title="Apprentices with paperwork not filed"
          line={w.paperwork ? 'Their mentor sees the checklist too. Look them up on People.' : 'Every apprentice has answered yes.'}
        />
        <LinkCard
          to="/books"
          count={booksLoaded ? (w.booksClosed === false ? 1 : 0) : null}
          title="Books"
          line={booksLine}
        />
      </div>

      <Section title="At a glance">
        <div className="kpi-grid lp-quiet-kpis">
          <Kpi label="Salons" value={k.salons} />
          <Kpi label="Mentors" value={k.mentors} />
          <Kpi label="Apprentices" value={k.apprentices} />
          <Kpi label="Lessons finished, last 7 days" value={k.lessons7d} />
          <Kpi label="Games turned on" value={k.games} />
        </div>
      </Section>
    </>
  );
}

function Kpi({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="kpi">
      <div className="kpi-top"><div className="kpi-label">{label}</div></div>
      <div className="kpi-num">{value == null ? '—' : value}</div>
    </div>
  );
}
